import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, useTenantsState, useTenantsActions } from '@frontegg/react';
import type { ITenantsResponseV2 } from '@frontegg/rest-api';
import { useAtomValue } from 'jotai';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { isDebugMode } from '../../debug/debugUser';
import { navigateTo } from '../../util/navigation';
import { getActiveTenantParents, getActiveTenantSubTenants } from '../queries/FronteggHierarchyQueries';
import { customerTenantIdState } from '../state/AuthState';
import { getCurrentSubdomain, parseMetadata } from './tenantSubdomain';

export interface TenantWithSubdomain extends ITenantsResponseV2 {
  subdomain?: string;
}

interface UseAccountSwitcherReturn {
  tenants: TenantWithSubdomain[];
  /** The account this session is on: the membership whose subdomain the host
   *  carries, which differs from the active account when a parent account is
   *  active. The active account stands in while no membership matches. */
  currentTenant?: ITenantsResponseV2;
  isLoading: boolean;
  isSwitching: boolean;
  error?: string;
  hasMultipleTenants: boolean;
  isInitialTenantLoad: boolean;
  handleSwitchTenant: (tenantId: string) => void;
}

const ACCOUNT_SWITCHED_PARAM = 'account_switched';
// The account the switch left, so the reload never hops straight back to it
const ACCOUNT_SWITCHED_FROM_PARAM = 'account_switched_from';
// Parentage changes rarely; an hour keeps every hook instance on one lookup per session
const HIERARCHY_STALE_TIME_MS = 60 * 60 * 1000;

/** The active account's place in the Frontegg hierarchy: the accounts beneath
 *  it, and the ancestors above it that the user belongs to, closest first. */
interface ActiveTenantHierarchy {
  subTenantIds: Set<string>;
  memberParentIds: string[];
}

function buildTenantUrl(subdomain: string): string {
  const hostname = window.location.hostname;
  const baseDomain = hostname === 'localhost' ? 'foresightdata.com' : hostname.split('.').slice(1).join('.');
  return `${window.location.protocol}//${subdomain}.${baseDomain}`;
}

function removeSwitchedParams(): void {
  const params = new URLSearchParams(window.location.search);
  if (!params.has(ACCOUNT_SWITCHED_PARAM) && !params.has(ACCOUNT_SWITCHED_FROM_PARAM)) {
    return;
  }
  params.delete(ACCOUNT_SWITCHED_PARAM);
  params.delete(ACCOUNT_SWITCHED_FROM_PARAM);
  const search = params.toString();
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (search ? `?${search}` : '') + window.location.hash
  );
}

// In debug mode, FronteggProvider is not in the tree so Frontegg hooks would crash.
// isDebugMode() is stable for the entire page lifetime (determined once at module load
// in index.tsx), so the hook call order is consistent across renders.
const debugStub: UseAccountSwitcherReturn = {
  tenants: [],
  currentTenant: undefined,
  isLoading: false,
  isSwitching: false,
  error: undefined,
  hasMultipleTenants: false,
  isInitialTenantLoad: false,
  handleSwitchTenant: () => {},
};

/* eslint-disable react-hooks/rules-of-hooks -- see comment above */
export function useAccountSwitcher(): UseAccountSwitcherReturn {
  if (isDebugMode()) {
    return debugStub;
  }
  const { user } = useAuth();
  const { tenants, activeTenant, loading, switchingTenant, error: tenantsError } = useTenantsState();
  const { switchTenant } = useTenantsActions();
  const { pushErrorToast } = useToastMessageState();
  const customerTenantId = useAtomValue(customerTenantIdState);
  const [error, setError] = useState<string | undefined>();
  const errorToastShownRef = useRef(false);
  const isSwitchingRef = useRef(false);
  const switchFailedRef = useRef(false);

  const userTenantIds = user?.tenantIds;
  const hasMultipleMemberships = (userTenantIds?.length ?? 0) > 1;
  const accessToken = user?.accessToken;
  const activeTenantId = activeTenant?.tenantId;

  // The tenant list is loaded by useCustomerTenantSync, which RouteWrapper
  // mounts above the navbar on every authenticated route

  // Parse tenants and extract subdomain from metadata
  const tenantsWithSubdomain: TenantWithSubdomain[] = useMemo(
    () =>
      tenants.map((tenant) => ({
        ...tenant,
        subdomain: parseMetadata(tenant.metadata)?.subdomain,
      })),
    [tenants]
  );

  // Only tenants with a subdomain are switchable; internal tenants (e.g. Starbreaker)
  // have no subdomain metadata and must not appear in or trigger the switcher
  const switchableTenants: TenantWithSubdomain[] = useMemo(
    () => tenantsWithSubdomain.filter((tenant) => tenant.subdomain),
    [tenantsWithSubdomain]
  );

  const hasMultipleTenants = switchableTenants.length > 1;

  // A parent account stays active on its sub-accounts' subdomains, so the
  // customer tenant, not the active account, is the one this session is on
  const currentTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === customerTenantId) ?? activeTenant,
    [tenants, customerTenantId, activeTenant]
  );

  // Frontegg's tenantsState.loading starts as true and only resolves once loadTenants()
  // runs, which the effect above triggers solely for users with multiple memberships —
  // so the raw membership count must gate this to avoid a permanent loading state for
  // single-tenant users
  const isInitialTenantLoad = loading && hasMultipleMemberships;

  // Only a user with several memberships can belong to a parent account, so
  // everyone else skips the lookups. The query is shared by every instance of
  // this hook (the navbar and the account menu), and a failed lookup resolves
  // to an empty hierarchy, which leaves the plain subdomain rule below in charge.
  const { data: hierarchyData } = useQuery({
    queryKey: ['frontegg-hierarchy', activeTenantId],
    enabled: hasMultipleMemberships && !!activeTenantId && !!accessToken,
    staleTime: HIERARCHY_STALE_TIME_MS,
    retry: false,
    queryFn: async () => {
      try {
        const [subTenants, parents] = await Promise.all([
          getActiveTenantSubTenants(accessToken ?? ''),
          getActiveTenantParents(accessToken ?? ''),
        ]);
        return { subTenantIds: subTenants.map((tenant) => tenant.tenantId), parents };
      } catch {
        return { subTenantIds: [], parents: [] };
      }
    },
  });

  const hierarchy = useMemo<ActiveTenantHierarchy | null>(() => {
    if (!hierarchyData) {
      return null;
    }
    const memberships = new Set(userTenantIds);
    return {
      subTenantIds: new Set(hierarchyData.subTenantIds),
      memberParentIds: [...hierarchyData.parents]
        .sort((a, b) => a.level - b.level)
        .filter((parent) => memberships.has(parent.tenantId))
        .map((parent) => parent.tenantId),
    };
  }, [hierarchyData, userTenantIds]);

  // Until the active account's hierarchy is known the switcher must neither
  // auto-switch nor let a parent user switch by hand
  const hierarchyPending = hasMultipleMemberships && !!activeTenantId && hierarchy === null;

  // Line the active account up with the subdomain on load
  useEffect(() => {
    if (loading || switchingTenant || tenantsWithSubdomain.length === 0) {
      return;
    }
    if (hierarchyPending) {
      return;
    }

    const currentSubdomain = getCurrentSubdomain();
    const activeSubdomain = activeTenant?.metadata ? parseMetadata(activeTenant.metadata)?.subdomain : null;
    const matchingTenant = tenantsWithSubdomain.find((tenant) => tenant.subdomain === currentSubdomain);

    // A parent account stays active on its sub-accounts' subdomains: its token
    // carries the roles held on the parent, and the customer API accepts it.
    if (matchingTenant && hierarchy?.subTenantIds.has(matchingTenant.tenantId)) {
      removeSwitchedParams();
      return;
    }

    // On the subdomain's own account, hand over to a parent the user belongs
    // to; anywhere else, move to the subdomain's account.
    const onSubdomainAccount = activeSubdomain === currentSubdomain;
    const targetId = onSubdomainAccount ? hierarchy?.memberParentIds[0] : matchingTenant?.tenantId;
    const target =
      targetId && targetId !== activeTenantId
        ? tenants.find((tenant) => tenant.tenantId === targetId)
        : undefined;
    if (!target) {
      if (onSubdomainAccount) {
        removeSwitchedParams();
      }
      return;
    }

    // One auto-switch per navigation, except a hop up from the subdomain's account
    // the last switch landed on (the hierarchy is only known for the active account),
    // unless that hop goes back to where the switch came from. The same target
    // still pending means the switch didn't take.
    const searchParams = new URLSearchParams(window.location.search);
    const switchedParam = searchParams.get(ACCOUNT_SWITCHED_PARAM);
    if (switchedParam !== null) {
      removeSwitchedParams();
      const isHopUpAfterSwitch =
        onSubdomainAccount &&
        switchedParam === activeTenantId &&
        searchParams.get(ACCOUNT_SWITCHED_FROM_PARAM) !== target.tenantId;
      if (!isHopUpAfterSwitch) {
        if (switchedParam === target.tenantId) {
          switchFailedRef.current = true;
          if (!errorToastShownRef.current) {
            errorToastShownRef.current = true;
            pushErrorToast({
              message: `Unable to automatically switch to the correct account. Please try switching manually.`,
            });
          }
        }
        return;
      }
    }

    // Guard against in-progress or already-failed switches this session
    if (isSwitchingRef.current || switchFailedRef.current) {
      return;
    }
    isSwitchingRef.current = true;

    switchTenant({
      tenantId: target.tenantId,
      callback: (_data, switchError) => {
        isSwitchingRef.current = false;
        if (switchError) {
          switchFailedRef.current = true;
          if (!errorToastShownRef.current) {
            errorToastShownRef.current = true;
            pushErrorToast({
              message: `Failed to switch to ${target.name}. Please try switching manually.`,
            });
          }
        } else {
          // Reload with the switched params in the URL so we can detect a failed switch on next load
          const reloadUrl = new URL(window.location.href);
          reloadUrl.searchParams.set(ACCOUNT_SWITCHED_PARAM, target.tenantId);
          if (activeTenantId) {
            reloadUrl.searchParams.set(ACCOUNT_SWITCHED_FROM_PARAM, activeTenantId);
          }
          window.location.replace(reloadUrl.toString());
        }
      },
    });
  }, [
    tenantsWithSubdomain,
    tenants,
    activeTenant,
    activeTenantId,
    hierarchy,
    hierarchyPending,
    loading,
    switchingTenant,
    switchTenant,
    pushErrorToast,
  ]);

  const handleSwitchTenant = useCallback(
    (tenantId: string) => {
      setError(undefined);

      const targetTenant = tenantsWithSubdomain.find((t) => t.tenantId === tenantId);

      if (!targetTenant) {
        setError('Tenant not found');
        return;
      }

      if (!targetTenant.subdomain) {
        setError(`Subdomain not configured for tenant: ${targetTenant.name}`);
        return;
      }

      const url = buildTenantUrl(targetTenant.subdomain);

      // A parent account reaches its sub-accounts on their subdomains without
      // giving up the parent's token
      if (hierarchy !== null && hierarchy.subTenantIds.has(tenantId)) {
        navigateTo(url);
        return;
      }

      switchTenant({
        tenantId,
        callback: () => navigateTo(url),
      });
    },
    [tenantsWithSubdomain, hierarchy, switchTenant]
  );

  return {
    tenants: switchableTenants,
    currentTenant,
    isLoading: loading || hierarchyPending,
    isSwitching: switchingTenant,
    error: error || (tenantsError ? 'Failed to load tenants' : undefined),
    hasMultipleTenants,
    isInitialTenantLoad,
    handleSwitchTenant,
  };
}
/* eslint-enable react-hooks/rules-of-hooks */
